import { Link } from 'react-router-dom'
import { useState } from 'react'
import { TopNav, Footer } from '../components/PublicLayout'
import SEO from '../components/SEO'
import Logo from '../components/Logo'
import { useAuth } from '../hooks/useAuth'
import { getArticlesSorted, getCategoryLabel, getArticlePrimaryCategory } from '../articles/index'

// ─── Image sources ────────────────────────────────────────────────────────────
// Images live in /public/img/ and are served at /img/[filename].
// To replace a photo, drop a new file into public/img/ and update the path below.

const IMG_HERO = '/img/receipts-pile.jpg'
const IMG_PROBLEM = '/img/emails.jpg'
const IMG_TRUST = '/img/bookkeeper.jpg'

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  const { user } = useAuth()
  return (
    <section style={{ padding: '64px 0 80px' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 56, alignItems: 'center',
        }}>
          {/* Left: text */}
          <div>
            <div style={{
              display: 'inline-block',
              background: 'var(--accent-light)', color: 'var(--accent)',
              padding: '5px 12px', borderRadius: 100,
              fontSize: '0.75rem', fontWeight: 600,
              marginBottom: 18, letterSpacing: '0.02em',
            }}>
              For UK bookkeepers
            </div>
            <h1 style={{ marginBottom: 18, lineHeight: 1.1 }}>
              Chase your clients once,<br/>
              <span style={{ color: 'var(--accent)' }}>not seven times.</span>
            </h1>
            <p style={{
              fontSize: '1.1rem', color: 'var(--ink-muted)',
              marginBottom: 30, lineHeight: 1.55, maxWidth: 500,
            }}>
              Send one link. Your client uploads every missing receipt in one
              place, even the lost ones. No more email threads, no more "I'll
              send it later", no more WhatsApp photos to dig through.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
              {user ? (
                <Link to="/dashboard" className="btn btn-primary" style={{ padding: '12px 24px', fontSize: '0.95rem' }}>
                  Open dashboard →
                </Link>
              ) : (
                <Link to="/signup" className="btn btn-primary" style={{ padding: '12px 24px', fontSize: '0.95rem' }}>
                  Start your free trial →
                </Link>
              )}
              <Link to="/features" className="btn btn-secondary" style={{ padding: '12px 24px', fontSize: '0.95rem' }}>
                See how it works
              </Link>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--ink-faint)' }}>
              No card required · Cancel any time · Built for UK practices
            </p>
          </div>

          {/* Right: stacked photo + screenshot */}
          <div style={{ position: 'relative', minHeight: 480 }}>
            {/* Photo — back layer, rotated slightly */}
            <div style={{
              position: 'absolute', top: 0, right: 0,
              width: '78%', aspectRatio: '4 / 5',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden', boxShadow: 'var(--shadow-lg)',
              transform: 'rotate(2deg)',
              border: '6px solid var(--white)',
            }}>
              <img
                src={IMG_HERO}
                alt="A pile of paper receipts and bookkeeping paperwork"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>

            {/* Screenshot — front layer, offset down-left */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0,
              width: '72%', borderRadius: 'var(--radius-lg)',
              background: 'var(--white)',
              boxShadow: 'var(--shadow-lg)',
              padding: '14px 16px',
              border: '1px solid var(--surface-2)',
              transform: 'rotate(-2deg)',
            }}>
              <ScreenshotMock />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// A stylised mini "screenshot" of the request page using the same components
// as the real app. Acts as a placeholder until you swap in a real screenshot.
function ScreenshotMock() {
  return (
    <div style={{ fontSize: '0.78rem', color: 'var(--ink)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        paddingBottom: 10, borderBottom: '1px solid var(--surface-2)',
        marginBottom: 12,
      }}>
        <Logo variant="icon" size={22} />
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '0.88rem' }}>Receipt Chaser</span>
      </div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>March 2026 Receipts</div>
      <div style={{ color: 'var(--ink-muted)', fontSize: '0.7rem', marginBottom: 10 }}>3 of 8 completed</div>
      <div style={{ height: 4, background: 'var(--surface-2)', borderRadius: 100, marginBottom: 14 }}>
        <div style={{ height: '100%', width: '37%', background: 'var(--accent)', borderRadius: 100 }} />
      </div>
      {[
        { d: '02 Mar', n: 'Mistral.ai', a: '-£13.04', s: 'done', f: 'Mistral_£13.04.pdf' },
        { d: '04 Mar', n: 'Tesco Express', a: '-£24.99', s: 'done', f: 'Tesco_£24.99.jpg' },
        { d: '11 Mar', n: 'OpenAI', a: '-£17.93', s: 'pending' },
        { d: '14 Mar', n: 'GoDaddy', a: '-£95.34', s: 'pending' },
      ].map((r, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '7px 0',
          borderBottom: i < 3 ? '1px solid var(--surface-2)' : 'none',
        }}>
          <div style={{
            width: 16, height: 16, borderRadius: 4,
            background: r.s === 'done' ? 'var(--green-light)' : 'var(--surface-2)',
            color: r.s === 'done' ? 'var(--green)' : 'var(--ink-faint)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
            marginTop: 2,
          }}>{r.s === 'done' ? '✓' : ''}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: 'var(--ink-faint)', fontSize: '0.65rem' }}>{r.d}</div>
            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {r.n}
            </div>
            {r.f && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                marginTop: 3, padding: '1px 6px', borderRadius: 4,
                background: 'var(--green-light)', color: 'var(--green)',
                fontSize: '0.6rem', fontWeight: 500,
                maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                📎 {r.f}
              </div>
            )}
          </div>
          <div style={{ fontWeight: 600, fontSize: '0.72rem', marginTop: 2 }}>{r.a}</div>
        </div>
      ))}
    </div>
  )
}

// ─── The problem section ──────────────────────────────────────────────────────

function ProblemSection() {
  return (
    <section style={{ padding: '64px 0', background: 'var(--white)' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 56, alignItems: 'center',
        }}>
          <div>
            <div style={{
              borderRadius: 'var(--radius-lg)', overflow: 'hidden',
              boxShadow: 'var(--shadow)', aspectRatio: '4 / 3',
            }}>
              <img
                src={IMG_PROBLEM}
                alt="A laptop showing an overflowing email inbox"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </div>
          <div>
            <p style={{
              fontSize: '0.78rem', fontWeight: 600,
              color: 'var(--accent)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 10,
            }}>
              The receipt-chase problem
            </p>
            <h2 style={{ marginBottom: 16 }}>Your client only sends back half the list.</h2>
            <p style={{ color: 'var(--ink-muted)', marginBottom: 20, lineHeight: 1.6 }}>
              You send them a clear list of every transaction needing a
              receipt. A week later you get back twelve photos, three PDFs,
              and a WhatsApp screenshot. Some are matched to your list. Most
              aren't. A few are duplicates of ones you already had.
            </p>
            <p style={{ color: 'var(--ink-muted)', marginBottom: 24, lineHeight: 1.6 }}>
              Then comes the back-and-forth: "Which Amazon order was that?",
              "Did you get the £47 one from Tesco?", "I think I sent that
              already." Three rounds of email later, you're still short two
              receipts and out of patience.
            </p>
            <p style={{ fontSize: '1.05rem', color: 'var(--ink)', fontWeight: 500 }}>
              Receipt Chaser shows your client the exact transactions you need
              receipts for, so nothing gets missed and every file lands against
              the right line.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── How it works ─────────────────────────────────────────────────────────────

function HowItWorks() {
  const steps = [
    {
      n: '1',
      title: 'Upload your list of missing receipts',
      body: 'Paste in the transactions you need receipts for from Xero, Sage, QuickBooks, or any UK bank export. Each one becomes a line for your client.',
    },
    {
      n: '2',
      title: 'Send your client one link',
      body: 'They open it on their phone, tablet or laptop, see the list of exactly what you need, upload each receipt, or mark one as lost and explain why.',
    },
    {
      n: '3',
      title: 'Download everything, renamed',
      body: 'When they\'re done you get a zip of every receipt, named by date, vendor and amount. Ready to file.',
    },
  ]
  return (
    <section id="how-it-works" style={{ padding: '80px 0' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48, maxWidth: 640, margin: '0 auto 48px' }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 10,
          }}>
            How it works
          </p>
          <h2>Three steps. About five minutes.</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 24,
        }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              background: 'var(--white)',
              padding: '32px 28px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--surface-2)',
              position: 'relative',
            }}>
              <div style={{
                width: 36, height: 36,
                background: 'var(--accent)', color: 'white',
                borderRadius: 100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--font-display)', fontSize: '1.05rem',
                marginBottom: 16, marginLeft: 'auto', marginRight: 'auto',
              }}>
                {s.n}
              </div>
              <h3 style={{ fontSize: '1.15rem', marginBottom: 8, textAlign: 'center' }}>{s.title}</h3>
              <p style={{ color: 'var(--ink-muted)', fontSize: '0.92rem', lineHeight: 1.55, textAlign: 'center' }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Features ─────────────────────────────────────────────────────────────────

function Features() {
  const items = [
    {
      icon: '🔗',
      title: 'Every receipt linked to a transaction',
      body: 'Your client knows exactly which transactions still need a receipt. You know exactly which receipt belongs to which line. No more "which Amazon order was that".',
    },
    {
      icon: '📥',
      title: 'Bulk upload, drag to match',
      body: 'Your client uploads every receipt at once, then drags each one onto the right transaction. Likely matches are auto-highlighted.',
    },
    {
      icon: '📱',
      title: 'Works on any phone',
      body: 'No app to install. They open the link, tap "Take photo", and the receipt is uploaded. Genuinely usable on mobile.',
    },
    {
      icon: '📁',
      title: 'Receipts arrive named',
      body: 'When you download, every file is renamed YYYY-MM-DD_Vendor_£Amount. Sorted, identifiable, ready for filing.',
    },
    {
      icon: '⚠',
      title: 'Lost receipts handled properly',
      body: 'When a receipt genuinely cannot be found, the client provides a written explanation and a "wholly business" declaration.',
    },
    {
      icon: '⚡',
      title: 'No account for your client',
      body: 'Your client opens a link and uploads. No sign-ups, no passwords, no "please verify your email" friction. Lower drop-off, faster receipts.',
    },
  ]
  return (
    <section id="features" style={{ padding: '80px 0', background: 'var(--white)' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 48, maxWidth: 640, margin: '0 auto 48px' }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 10,
          }}>
            What you get
          </p>
          <h2>Built for the actual workflow, not a generic file upload.</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 24,
        }}>
          {items.map((f, i) => (
            <div key={i} style={{
              padding: '28px 24px',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--surface-2)',
              background: 'var(--surface)',
            }}>
              <div style={{ fontSize: '1.6rem', marginBottom: 12, textAlign: 'center' }}>{f.icon}</div>
              <h3 style={{ fontSize: '1.05rem', marginBottom: 8, textAlign: 'center' }}>{f.title}</h3>
              <p style={{ color: 'var(--ink-muted)', fontSize: '0.88rem', lineHeight: 1.55 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Trust / reassurance section ─────────────────────────────────────────────

function TrustSection() {
  return (
    <section style={{ padding: '80px 0' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 56, alignItems: 'center',
        }}>
          <div>
            <p style={{
              fontSize: '0.78rem', fontWeight: 600,
              color: 'var(--accent)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 10,
            }}>
              Built for your practice
            </p>
            <h2 style={{ marginBottom: 20 }}>Made by a working bookkeeper, for working bookkeepers.</h2>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                'Built around UK bookkeeping: £ amounts, UK date formats, and how UK practices actually work.',
                'Add your whole team. Practice users are unlimited on every plan.',
                'Files are securely stored. We never sell data, never run ads, never share your client information.',
                'No long contracts. Pay monthly, cancel any time.',
              ].map((line, i) => (
                <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 22, height: 22, background: 'var(--green-light)',
                    color: 'var(--green)', borderRadius: 100, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem', fontWeight: 700, marginTop: 1,
                  }}>✓</div>
                  <span style={{ color: 'var(--ink-muted)', fontSize: '0.95rem', lineHeight: 1.55 }}>
                    {line}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div style={{
              borderRadius: 'var(--radius-lg)', overflow: 'hidden',
              boxShadow: 'var(--shadow)', aspectRatio: '4 / 3',
            }}>
              <img
                src={IMG_TRUST}
                alt="A bookkeeper at work in an office"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Final CTA ────────────────────────────────────────────────────────────────

function FinalCTA() {
  const { user } = useAuth()
  return (
    <section style={{ padding: '80px 0', background: 'var(--white)' }}>
      <div className="container" style={{ padding: '0 24px' }}>
        <div style={{
          background: 'var(--ink)',
          borderRadius: 'var(--radius-lg)',
          padding: 'clamp(40px, 6vw, 64px) clamp(28px, 5vw, 64px)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(72,146,145,0.25), transparent 50%)',
            pointerEvents: 'none',
          }} />
          <div style={{ position: 'relative' }}>
            <h2 style={{ color: 'white', marginBottom: 14 }}>
              Get back the hours you spend chasing.
            </h2>
            {user ? (
              <>
                <p style={{
                  color: 'rgba(255,255,255,0.8)', fontSize: '1.05rem',
                  marginBottom: 28, maxWidth: 540, margin: '0 auto 28px',
                }}>
                  Jump back into your dashboard to send the next request.
                </p>
                <Link to="/dashboard" className="btn btn-primary" style={{ padding: '14px 28px', fontSize: '1rem' }}>
                  Open dashboard →
                </Link>
              </>
            ) : (
              <>
                <p style={{
                  color: 'rgba(255,255,255,0.8)', fontSize: '1.05rem',
                  marginBottom: 28, maxWidth: 540, margin: '0 auto 28px',
                }}>
                  Start a free trial. Send your first request in under five minutes.
                  No card required.
                </p>
                <Link to="/signup" className="btn btn-primary" style={{ padding: '14px 28px', fontSize: '1rem' }}>
                  Start your free trial →
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Articles preview ─────────────────────────────────────────────────────────
//
// Three most recent articles displayed as cards. Positioned just above the
// Footer to keep the sales-focused sections at the top of the page unbroken,
// while still giving Google strong internal links to /articles and to each
// article page (better SEO weight than footer-only links).
//
// Card design intentionally mirrors the articles index page so the homepage
// and /articles feel like the same property. If the card style changes there,
// update here too (or extract into a shared component).

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

function ArticlesPreview() {
  // Pull the 3 newest articles. Date-sort is handled in the registry helper,
  // so this is just a slice of the top of that list.
  const articles = getArticlesSorted().slice(0, 3)

  // If somehow there are no articles published yet, render nothing rather than
  // an empty section. Defensive: the registry always has entries today, but
  // future maintainers may unpublish.
  if (articles.length === 0) return null

  return (
    <section style={{ padding: '80px 0', background: 'var(--surface)' }}>
      <div className="container" style={{ padding: '0 24px', maxWidth: 1080 }}>
        {/* Section header. Matches the eyebrow+H2 pattern used on the articles
            index page so the visual identity is consistent across the site. */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.08em', marginBottom: 12,
            marginTop: 0,
          }}>
            Articles
          </p>
          <h2 style={{
            margin: 0,
            fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)',
            lineHeight: 1.2,
          }}>
            Practical guides for UK bookkeepers
          </h2>
        </div>

        {/* Card grid. auto-fit responsive so the row breaks naturally on
            narrow screens (each card needs at least ~280px to be readable).
            On desktop this renders as three columns; on mobile, a single
            column stack. */}
        <ul style={{
          listStyle: 'none', padding: 0, margin: '0 0 40px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
        }}>
          {articles.map(article => (
            <li key={article.slug}>
              <Link
                to={`/articles/${article.slug}`}
                style={{
                  display: 'block',
                  padding: '24px 26px',
                  background: 'var(--white)',
                  borderRadius: 12,
                  border: '1px solid var(--surface-2)',
                  textDecoration: 'none',
                  color: 'inherit',
                  height: '100%',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'var(--accent)'
                  e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--surface-2)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <div style={{
                  display: 'flex', gap: 10, alignItems: 'center',
                  fontSize: '0.74rem', color: 'var(--ink-muted)',
                  marginBottom: 10, letterSpacing: '0.02em',
                }}>
                  <span>{formatDate(article.date)}</span>
                  {getCategoryLabel(getArticlePrimaryCategory(article)) && (
                    <>
                      <span style={{ color: 'var(--ink-faint)' }}>·</span>
                      <span style={{
                        color: 'var(--accent)', fontWeight: 600,
                        textTransform: 'uppercase', fontSize: '0.7rem',
                        letterSpacing: '0.05em',
                      }}>
                        {getCategoryLabel(getArticlePrimaryCategory(article))}
                      </span>
                    </>
                  )}
                </div>
                <h3 style={{
                  fontSize: '1.1rem', margin: '0 0 8px',
                  color: 'var(--ink)', lineHeight: 1.35,
                }}>
                  {article.title}
                </h3>
                <p style={{
                  margin: 0, color: 'var(--ink-muted)',
                  fontSize: '0.9rem', lineHeight: 1.55,
                }}>
                  {article.description}
                </p>
              </Link>
            </li>
          ))}
        </ul>

        {/* "View all" link sits on its own line, low-key. Google reads this
            as an additional internal link to /articles (strengthening the
            crawl depth of the index page). For readers, it's the discovery
            path to articles not shown in the preview. */}
        <div style={{ textAlign: 'center' }}>
          <Link
            to="/articles"
            style={{
              fontSize: '0.95rem',
              color: 'var(--accent)',
              textDecoration: 'none',
              fontWeight: 500,
            }}
          >
            View all articles →
          </Link>
        </div>
      </div>
    </section>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Home() {
  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title="Receipt collection on autopilot for UK bookkeepers"
        description="Receipt Chaser collects missing receipts & invoices for bookkeepers. Upload your CSV, send one link, get audit-ready files back. Free 30-day trial."
        path="/"
      />
      <TopNav />
      <Hero />
      <ProblemSection />
      <HowItWorks />
      <Features />
      <TrustSection />
      <FinalCTA />
      <ArticlesPreview />
      <Footer />
    </div>
  )
}
